import { Canceler } from 'axios';
import { action, computed, makeObservable, observable } from 'mobx';
import { ITEM_TYPE, LOCALES, MEASURE, defaulLangTextValidation, defaultPagination } from 'src/constants/main';
import { SCREENS } from 'src/constants/screens';
import client from 'src/services/client';
import { history } from 'src/services/history';
import { ICountsResponce, IPagination, TBooleanInNumber } from 'src/types/main';
import {
  booleanNumberReverse,
  countsUpdate,
  getAvailabilityParams,
  getCancelToken,
  getTranslate,
  randomFloatNumber,
  scrollToErrorElement,
} from 'src/utils/helpers';
import { caloriesValidation, emptyNameValidation, emptyPriceValidation, sameNameValidation } from 'src/utils/validation';
import {
  defaultModifier,
  defaultModifierOption,
  defaultModifierValidation,
  defaultModifiersFilters,
} from 'src/view/screens/MenuManagementScreen/screens/ModifiersScreen/constants/modifiers';
import {
  IModifier,
  IModifierOption,
  IModifierOptionValidation,
  IModifiersFilters,
  MODIFIERS_CHECKBOX,
} from 'src/view/screens/MenuManagementScreen/screens/ModifiersScreen/types/modifiers';
import { localeStore } from '~/mobx/localesStore';
import logger from '~/services/logger';
import { toastSuccess } from '~/utils/toasts';

class ModifiersStore {
  constructor() {
    makeObservable(this);
  }

  cancelStack: {
    [key: string]: Canceler;
  } = {};

  @observable pagination: IPagination<IModifier> = defaultPagination;
  @observable modifier: IModifier | null = null;
  @observable edited: boolean = false;
  @observable filters: IModifiersFilters = defaultModifiersFilters;
  @action setFilters = (filters: Partial<IModifiersFilters>) => {
    this.filters = { ...this.filters, ...filters };
    this.fetchModifiers(1);
  };

  @computed get isLoading(): boolean {
    return Boolean(this.pagination.loading || this.pagination.refreshing);
  }

  @action fetchModifiers = async (page: number = this.pagination.current_page + 1) => {
    try {
      const cancelId = 'fetchModifiers';
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');

      const params: any = {
        page,
        name: this.filters.search,
        is_delete: 0,
        sort_field: this.filters.sort_field,
        sort_dir: this.filters.sort_dir,
        ...getAvailabilityParams(this.filters.availability),
      };

      this.pagination.loading = true;
      this.pagination.refreshing = true;
      const pagination = await client.get('v3/modifiers', {
        params,
        headers: { 'Accept-Language': 'en,ar' },
        cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)),
      });

      this.pagination = {
        ...pagination,
        data: pagination.data,
        loading: false,
        refreshing: false,
      };
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.pagination = {
        ...this.pagination,
        loading: false,
        refreshing: false,
      };
      logger.error({ error, place: 'ModifiersStore fetchModifiers' });
    }
  };

  public modifiersCount: number | undefined = undefined;
  public foundModifiersInSystem: boolean = false;
  public fetchModifiersCount = async () => {
    try {
      const counts: ICountsResponce = await client.get(`v3/modifiers_count`);
      this.modifiersCount = counts.available;
      this.foundModifiersInSystem = Boolean(counts.available + counts.deleted);
    } catch (error: any) {
      logger.error({ error, place: 'ModifiersStore fetchModifiersCount' });
    }
  };

  @action getModifier = async (id: string | number) => {
    try {
      this.pagination.loading = true;
      const modifier: IModifier = await client.get(`v3/modifiers/${id}`, { headers: { 'Accept-Language': 'en,ar' } });
      this.setModifier(modifier && modifier.id ? this.transformModifier(modifier) : undefined);
      this.pagination.loading = false;
    } catch (error: any) {
      this.pagination.loading = false;
      logger.error({ error, place: 'ModifiersStore getModifier', showModal: true });
    }
  };

  @action setModifier = (modifier?: IModifier) => {
    this.modifier = modifier ? this.transformModifier(modifier) : this.defaultModifier;
  };
  private get defaultModifier(): IModifier {
    const count: string = String((this.modifiersCount ?? this.pagination.total) + 1);

    return {
      ...defaultModifier,
      name: {
        en: getTranslate('modifier', LOCALES.EN) + ' ' + count,
        ar: getTranslate('modifier', LOCALES.AR) + ' ' + count,
      },
    };
  }
  private transformModifier = (modifier: IModifier): IModifier => {
    return {
      ...modifier,
      external_id: modifier.external_id?.toString(),
      options: modifier.options.filter((option) => !option.is_delete).map((option) => this.transformOption(option)),
    };
  };
  private transformOption = (option: IModifierOption): IModifierOption => {
    return {
      ...option,
      price: option.price.toString(),
      weight: option.weight.toString(),
      unit: option.unit || MEASURE.NULL,
      external_id: option.external_id?.toString(),
    };
  };

  // main details block
  @action changeModifierName = (value: string, currentLanguage: LOCALES) => {
    if (!this.modifier) return;
    this.modifier.name = { ...this.modifier.name, [currentLanguage]: value };
    this.edited = true;
  };
  @action changeModifierExternalId = (value: string | number) => {
    if (!this.modifier) return;
    this.modifier.external_id = value.toString();
    this.edited = true;
  };
  @action changeModifierAvailability = (status: TBooleanInNumber) => {
    if (!this.modifier) return;
    this.modifier.status = status;
    this.edited = true;
  };
  // mainDetails block end

  // options block
  @action addOption = () => {
    if (!this.modifier) return;

    this.modifier.options.push({ ...defaultModifierOption, id: randomFloatNumber() });
    this.edited = true;
  };
  @action deleteOption = (id: number) => {
    if (!this.modifier) return;

    this.modifier.options = this.modifier.options.filter((option) => option.id !== id);
    this.edited = true;
  };
  @action updateOption = (id: number, value: Partial<IModifierOption>) => {
    if (!this.modifier) return;
    this.modifier.options = this.modifier.options.map((option) => (option.id === id ? { ...option, ...value } : option));
    this.edited = true;
  };
  @action changePricesCheckBox = (name: string | number) => {
    if (!this.modifier) return;
    if (name === MODIFIERS_CHECKBOX.APPLY_PRICE_IN_LINKED_ITEMS) {
      this.modifier.apply_price_in_linked_items = booleanNumberReverse(this.modifier.apply_price_in_linked_items);
      if (!this.modifier.apply_price_in_linked_items) this.modifier.apply_price_in_branches = 0;
    } else if (name === MODIFIERS_CHECKBOX.APPLY_PRICE_IN_BRANCHES) {
      this.modifier.apply_price_in_branches = booleanNumberReverse(this.modifier.apply_price_in_branches);
    }
    this.edited = true;
  };
  // options block end

  @action createModifier = async () => {
    try {
      if (!this.modifier) return;

      this.pagination.loading = true;

      await client.post(`v3/modifiers`, this.params);

      this.edited = false;
      history.push(SCREENS.MODIFIERS());

      toastSuccess({ message: localeStore.t('toast_modifier_changes_saved') });
    } catch (error: any) {
      this.pagination.loading = false;

      // name validation
      [this.validation.name, this.validation.valid] = sameNameValidation({ name: this.validation.name, type: ITEM_TYPE.MODIFIER, error });
      if (!this.validation.valid) {
        scrollToErrorElement();
        return;
      }

      logger.error({ error, place: 'ModifiersStore createModifier', showModal: true });
    }
  };

  @action updateModifier = async (id: number) => {
    try {
      if (!this.modifier) return;

      this.pagination.loading = true;

      await client.put(`v3/modifiers/${id}`, this.params);

      this.edited = false;
      history.push(SCREENS.MODIFIERS());

      toastSuccess({ message: localeStore.t('toast_modifier_changes_saved') });
    } catch (error: any) {
      this.pagination.loading = false;

      // name validation
      [this.validation.name, this.validation.valid] = sameNameValidation({ name: this.validation.name, type: ITEM_TYPE.MODIFIER, error });
      if (!this.validation.valid) {
        scrollToErrorElement();
        return;
      }

      logger.error({ error, place: 'ModifiersStore updateModifier', showModal: true });
    }
  };

  private get params() {
    if (!this.modifier) return {};

    return {
      ...this.modifier,
      id: this.modifier.id || undefined,
      external_id: this.modifier.external_id || null,
      is_draft: undefined,
      options: this.modifier.options.map((option) => {
        return {
          ...option,
          id: Number.isInteger(option.id) ? option.id : undefined,
          weight: option.weight || 0,
          external_id: option.external_id || null,
        };
      }),
    };
  }

  @action changeStatus = async (id: number, status: TBooleanInNumber) => {
    try {
      const cancelId = `status_${id}`;
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');

      this.pagination.data = this.pagination.data.map((modifier) => (modifier.id === id ? { ...modifier, status } : modifier));

      await client.put(`v3/modifiers/${id}`, { status }, { cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)) });

      toastSuccess({ message: localeStore.t('toast_modifier_changes_saved') });
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.pagination.data = this.pagination.data.map((modifier) =>
        modifier.id === id ? { ...modifier, status: booleanNumberReverse(modifier.status) } : modifier
      );
      logger.error({ error, place: 'ModifiersStore changeStatus' });
    }
  };

  @action restoreModifier = async (modifierId: number) => {
    try {
      const cancelId = `restore_${modifierId}`;
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');

      this.pagination.refreshing = true;

      await client.patch(`v3/modifiers/${modifierId}`, {}, { cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)) });

      this.fetchModifiers(this.pagination.current_page);
      this.modifiersCount = countsUpdate(this.modifiersCount, true);

      toastSuccess({ message: localeStore.t('toast_modifier_restored') });
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.pagination.refreshing = false;
      logger.error({ error, place: 'ModifiersStore restoreModifier', showModal: true });
    }
  };

  @action deleteModifier = async (modifierId: number) => {
    try {
      const cancelId = `delete_${modifierId}`;
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');

      this.pagination.refreshing = true;

      await client.delete(`v3/modifiers/${modifierId}`, { cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)) });

      this.fetchModifiers(this.pagination.current_page);
      this.modifiersCount = countsUpdate(this.modifiersCount);

      toastSuccess({ message: localeStore.t('toast_modifier_deleted') });
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.pagination.refreshing = false;
      logger.error({ error, place: 'ModifiersStore deleteModifier', showModal: true });
    }
  };

  @observable validation = defaultModifierValidation;
  @action setValidation = () => {
    if (!this.modifier) return;

    this.validation = { ...defaultModifierValidation };

    // name validation
    [this.validation.name, this.validation.valid] = emptyNameValidation(this.modifier.name, this.validation.name, this.validation.valid);

    // options validation
    if (this.modifier.options.length) {
      this.validation.options = this.modifier.options.map((option) => {
        const _option: IModifierOptionValidation = {
          id: option.id,
          price: { state: true, errorText: undefined },
          calories: { state: true, errorText: undefined },
          name: { ...defaulLangTextValidation },
        };

        // options price validation
        [_option.price, this.validation.valid] = emptyPriceValidation(option.price, _option.price, this.validation.valid);

        // options calories validation
        [_option.calories, this.validation.valid] = caloriesValidation(option.calories, _option.calories, this.validation.valid);

        // options name validation
        [_option.name, this.validation.valid] = emptyNameValidation(option.name, _option.name, this.validation.valid);
        // commented out, because https://taker.atlassian.net/browse/RD-944
        // [_option.name, this.validation.valid] = sameNameValidation({
        //   isValid: this.validation.valid,
        //   name: _option.name,
        //   type: ITEM_TYPE.MODIFIER_OPTION,
        //   itemArray: this.modifier?.options,
        //   item: option,
        // });
        return _option;
      });
    }
  };

  @action unMount = () => {
    this.modifier = null;
    this.edited = false;
    this.validation = defaultModifierValidation;

    if (!window.location.pathname.includes(SCREENS.MODIFIERS())) this.filters = defaultModifiersFilters;
  };
}

const modifiersStore = new ModifiersStore();

export default modifiersStore;
